(() => {
  const GLOBAL_KEY = 'RAEditorialCollectionView';

  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].sync === 'function') {
    window[GLOBAL_KEY].sync();
    return;
  }

  const MOBILE_BREAKPOINT = 768;
  const STORAGE_KEY = 'raCollectionEditorialView';
  const TOOLBAR_SELECTOR = '.m-toolbar--column-switcher';
  const NUMERIC_BUTTON_SELECTOR = `${TOOLBAR_SELECTOR} button[data-column]`;
  const EDITORIAL_BUTTON_SELECTOR = `${TOOLBAR_SELECTOR} button[data-ra-editorial-view]`;
  const PRODUCT_CONTAINER_SELECTOR = '[data-product-container]';
  const LAYOUT_CLASS = 'm-layout-editorial';
  const PRODUCT_CLASS = 'm-product-item--editorial-product';
  const FEATURED_CLASS = 'm-product-item--editorial-featured';
  const FEATURE_INTERVALS = [4, 6, 4, 8, 6];
  const GRID_CLASSES = ['m-cols-1', 'm-cols-2', 'm-cols-3', 'm-cols-4', 'm-cols-5', 'm-cols-6'];

  let editorialActive = false;
  let previousColumn = null;
  let observer = null;
  let scheduledFrame = null;
  let resizeTimer = null;
  let lastPatternContainer = null;
  let lastPatternSignature = '';

  const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;

  const readPreference = () => {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) !== 'false';
    } catch (error) {
      return true;
    }
  };

  const savePreference = (enabled) => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
    } catch (error) {
      // Storage can be unavailable in restrictive/private browsing modes.
    }
  };

  const getProductContainer = () => document.querySelector(PRODUCT_CONTAINER_SELECTOR);

  const isProductItem = (item) => {
    return (
      item instanceof HTMLElement &&
      item.classList.contains('m-product-item') &&
      !isEditorialBoundary(item) &&
      Boolean(item.querySelector('.m-product-card'))
    );
  };

  const isEditorialBoundary = (item) => {
    return (
      item instanceof HTMLElement &&
      (item.classList.contains('ra-code-extension-grid-video') ||
        item.hasAttribute('data-ra-code-extension-grid-video') ||
        item.hasAttribute('data-ra-look-insert') ||
        item.hasAttribute('data-ra-arrivals-grid-video'))
    );
  };

  const getProductItems = (container) => {
    if (!container) return [];
    return Array.from(container.children).filter(isProductItem);
  };

  const getProductKey = (item, index) => {
    const card = item.querySelector('.m-product-card');
    const productId = card?.dataset.productId || card?.getAttribute('data-product-id');
    const productLink = card?.querySelector('a[href*="/products/"]')?.getAttribute('href');
    return productId || productLink || String(index);
  };

  const getCurrentColumn = (container) => {
    const activeButton = document.querySelector(`${NUMERIC_BUTTON_SELECTOR}.active`);
    const activeColumn = Number(activeButton?.dataset.column);
    if (activeColumn >= 1 && activeColumn <= 6) return activeColumn;

    for (let column = 1; column <= 6; column += 1) {
      if (container?.classList.contains(`m-cols-${column}`)) return column;
    }

    return 2;
  };

  const removeGridClasses = (container) => {
    if (!container) return;
    container.classList.remove(...GRID_CLASSES);
  };

  const resetPatternMemory = () => {
    lastPatternContainer = null;
    lastPatternSignature = '';
  };

  const clearEditorialClasses = (container) => {
    if (!container) {
      resetPatternMemory();
      return;
    }

    container.classList.remove(LAYOUT_CLASS);
    container
      .querySelectorAll(`.${PRODUCT_CLASS}, .${FEATURED_CLASS}`)
      .forEach((item) => item.classList.remove(PRODUCT_CLASS, FEATURED_CLASS));

    resetPatternMemory();
  };

  const refreshResponsiveImages = (container) => {
    if (!container) return;

    window.requestAnimationFrame(() => {
      container.querySelectorAll('responsive-image').forEach((image) => {
        if (!image.hasAttribute('intersecting')) return;
        const img = image.querySelector('img');
        const width = image.clientWidth;
        const height = image.clientHeight;
        if (!img || !width) return;
        if (img.width !== width) img.width = width;
        if (img.height !== height) img.height = height;
        if (img.hasAttribute('srcset') && img.sizes !== `${width}px`) {
          img.sizes = `${width}px`;
        }
      });
    });
  };

  const updateButtonState = () => {
    const editorialButton = document.querySelector(EDITORIAL_BUTTON_SELECTOR);
    if (editorialButton) {
      const selected = editorialActive && isMobile();
      editorialButton.classList.toggle('active', selected);
      editorialButton.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }

    if (editorialActive && isMobile()) {
      document
        .querySelectorAll(NUMERIC_BUTTON_SELECTOR)
        .forEach((button) => button.classList.remove('active'));
    }
  };

  const buildEditorialPlan = (container) => {
    const allItems = [];
    const segments = [];
    let currentSegment = [];

    Array.from(container.children).forEach((item) => {
      if (!(item instanceof HTMLElement) || !item.classList.contains('m-product-item')) return;
      if (item.hidden || window.getComputedStyle(item).display === 'none') return;

      if (isEditorialBoundary(item)) {
        if (currentSegment.length > 0) segments.push(currentSegment);
        currentSegment = [];
        return;
      }

      allItems.push(item);
      currentSegment.push(item);
    });

    if (currentSegment.length > 0) segments.push(currentSegment);

    const featuredItems = new Set();

    segments.forEach((items) => {
      const candidates = [];
      let intervalIndex = 0;
      let halfWidthCount = 0;

      items.forEach((item) => {
        if (
          isProductItem(item) &&
          halfWidthCount >= FEATURE_INTERVALS[intervalIndex] &&
          halfWidthCount % 2 === 0
        ) {
          candidates.push(item);
          halfWidthCount = 0;
          intervalIndex = (intervalIndex + 1) % FEATURE_INTERVALS.length;
        } else {
          halfWidthCount += 1;
        }
      });

      if (halfWidthCount % 2 !== 0) {
        if (candidates.length > 0) {
          candidates.pop();
        } else {
          candidates.push(items[items.length - 1]);
        }
      }

      candidates.forEach((item) => featuredItems.add(item));
    });

    return { allItems, featuredItems };
  };

  const applyEditorialPattern = () => {
    const container = getProductContainer();
    if (!container) return false;

    const { allItems, featuredItems } = buildEditorialPlan(container);
    const signature = allItems
      .map((item, index) => `${getProductKey(item, index)}:${featuredItems.has(item) ? '1' : '0'}`)
      .join('|');

    const layoutWasMissing = !container.classList.contains(LAYOUT_CLASS);
    const patternChanged =
      container !== lastPatternContainer ||
      signature !== lastPatternSignature ||
      layoutWasMissing;

    const plannedItems = new Set(allItems);
    Array.from(container.children).forEach((item) => {
      if (!plannedItems.has(item)) item.classList.remove(PRODUCT_CLASS, FEATURED_CLASS);
    });

    allItems.forEach((item) => {
      item.classList.add(PRODUCT_CLASS);
      item.classList.toggle(FEATURED_CLASS, featuredItems.has(item));
    });

    container.classList.add(LAYOUT_CLASS);
    removeGridClasses(container);
    container.classList.add('m-cols-2');

    lastPatternContainer = container;
    lastPatternSignature = signature;

    return patternChanged;
  };

  const createEditorialButton = () => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'm:flex md:m:hidden m-tooltip m-tooltip--top m-editorial-view-button';
    button.dataset.raEditorialView = '';
    button.setAttribute('aria-label', 'Visualizzazione editoriale');
    button.setAttribute('aria-pressed', 'false');
    button.title = 'Visualizzazione editoriale';
    button.innerHTML = `
      <svg class="m-svg-icon--small" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false">
        <rect x="1" y="1" width="7" height="6" rx="1" stroke="currentColor" stroke-width="1.5"></rect>
        <rect x="10" y="1" width="7" height="6" rx="1" stroke="currentColor" stroke-width="1.5"></rect>
        <rect x="1" y="10" width="16" height="7" rx="1" stroke="currentColor" stroke-width="1.5"></rect>
      </svg>
      <span class="m-tooltip__content">Editoriale</span>
    `;
    button.addEventListener('click', () => activateEditorialView());
    return button;
  };

  const mountButton = () => {
    const toolbar = document.querySelector(TOOLBAR_SELECTOR);
    if (!toolbar) return null;

    toolbar.classList.add('ra-has-editorial-view');

    let button = toolbar.querySelector('[data-ra-editorial-view]');
    if (!button) {
      button = createEditorialButton();
      // Keep all five native numeric buttons in their original order. Appending
      // after them also preserves the theme's numeric button indexing.
      toolbar.appendChild(button);
    }

    return button;
  };

  const activateEditorialView = ({ persist = true } = {}) => {
    if (!isMobile()) return;

    const container = getProductContainer();
    const button = mountButton();
    if (!container || !button) return;

    if (!editorialActive) {
      previousColumn = getCurrentColumn(container);
    }

    editorialActive = true;
    if (persist) savePreference(true);

    const patternChanged = applyEditorialPattern();
    updateButtonState();
    if (patternChanged) refreshResponsiveImages(container);
  };

  const deactivateEditorialView = ({ persist = true, restoreColumn = true } = {}) => {
    const container = getProductContainer();
    const wasActive = editorialActive;

    editorialActive = false;
    if (persist) savePreference(false);

    clearEditorialClasses(container);

    if (container) {
      removeGridClasses(container);
      if (restoreColumn) {
        const column = previousColumn || 2;
        container.classList.add(`m-cols-${column}`);
        document.querySelectorAll(NUMERIC_BUTTON_SELECTOR).forEach((button) => {
          button.classList.toggle('active', Number(button.dataset.column) === column);
        });
      }
    }

    previousColumn = null;
    updateButtonState();

    if (wasActive && restoreColumn) {
      refreshResponsiveImages(container);
    }
  };

  const sync = () => {
    mountButton();

    if (editorialActive && isMobile()) {
      const patternChanged = applyEditorialPattern();
      updateButtonState();
      if (patternChanged) refreshResponsiveImages(getProductContainer());
      return;
    }

    updateButtonState();
  };

  const scheduleSync = () => {
    if (scheduledFrame) window.cancelAnimationFrame(scheduledFrame);
    scheduledFrame = window.requestAnimationFrame(() => {
      scheduledFrame = null;
      sync();
    });
  };

  const startObserver = () => {
    if (observer) observer.disconnect();

    const root = document.getElementById('MainContent') || document.body;
    observer = new MutationObserver(scheduleSync);
    observer.observe(root, { childList: true, subtree: true });
  };

  document.addEventListener(
    'click',
    (event) => {
      const numericButton = event.target.closest?.(NUMERIC_BUTTON_SELECTOR);
      if (!numericButton || !editorialActive) return;

      // Clear the editorial classes before the theme's native delegated click
      // handler applies the selected 1/2/3-column view.
      deactivateEditorialView({ persist: true, restoreColumn: false });
    },
    true
  );

  document.addEventListener('shopify:section:load', scheduleSync);
  document.addEventListener('shopify:section:reorder', scheduleSync);

  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      if (!isMobile() && editorialActive) {
        deactivateEditorialView({ persist: false, restoreColumn: true });
      } else if (isMobile() && !editorialActive && readPreference()) {
        activateEditorialView({ persist: false });
      } else {
        scheduleSync();
      }
    }, 180);
  });

  const initialize = () => {
    mountButton();
    startObserver();
    if (isMobile() && readPreference()) {
      activateEditorialView({ persist: false });
    } else {
      updateButtonState();
    }
  };

  window[GLOBAL_KEY] = {
    sync: scheduleSync,
    activate: activateEditorialView,
    deactivate: deactivateEditorialView,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  window.addEventListener('load', () => {
    if (isMobile() && readPreference()) {
      activateEditorialView({ persist: false });
    } else {
      scheduleSync();
    }
  });
})();
